/**
 * Print view of calendar days (Outlook/Google "Print"): the events per day as a list (time, title, location,
 * source calendar) in one printable document; print it with `printHtml`. Print colours are fixed black on white.
 */
import { escapeHtml } from '../mail/sanitize';
import type { CalendarGridEvent } from './model';

export interface CalendarPrintLabels {
  title: string;
  allDay: string;
  /** Text of a day without events. */
  empty: string;
  dayLabel: (day: Date) => string;
  timeRange: (event: CalendarGridEvent) => string;
  /** Calendar name (or empty). */
  source: (event: CalendarGridEvent) => string;
}

export function calendarPrintDocument(days: readonly Date[], events: readonly CalendarGridEvent[], labels: CalendarPrintLabels): string {
  const next = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  const sections = days.map((day) => {
    const list = events
      .filter((e) => e.start < next(day) && (e.end > day || e.start >= day))
      .sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.getTime() - b.start.getTime());
    const rows = list
      .map(
        (e) =>
          `<tr><td class="time">${escapeHtml(e.allDay ? labels.allDay : labels.timeRange(e))}</td><td><strong>${escapeHtml(e.title)}</strong>${e.detail ? `<br><span class="muted">${escapeHtml(e.detail)}</span>` : ''}</td><td class="muted">${escapeHtml(labels.source(e))}</td></tr>`,
      )
      .join('');
    return `<section><h2>${escapeHtml(labels.dayLabel(day))}</h2>${rows ? `<table>${rows}</table>` : `<p class="muted">${escapeHtml(labels.empty)}</p>`}</section>`;
  });
  const style =
    'body{font:12px/1.4 system-ui,sans-serif;color:black;margin:16px}h1{font-size:18px;margin:0 0 12px}h2{font-size:13px;margin:12px 0 4px;border-bottom:1px solid gray}' +
    'table{width:100%;border-collapse:collapse}td{padding:3px 6px;vertical-align:top;border-bottom:1px solid lightgray}.time{width:9em;white-space:nowrap}.muted{color:dimgray}section{break-inside:avoid}';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(labels.title)}</title><style>${style}</style></head><body><h1>${escapeHtml(labels.title)}</h1>${sections.join('')}</body></html>`;
}
